import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDrawerMode } from '@angular/material/sidenav';
import { BehaviorSubject, distinctUntilChanged, Observable, shareReplay, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DrawerService implements OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly leftNavCollapsedStorageKey = 'left-nav-collapsed';
  private readonly _opened$ = new BehaviorSubject(false);
  private readonly _leftNavCollapsed$ = new BehaviorSubject(this.getInitialLeftNavCollapsed());
  private readonly _mode$ = new BehaviorSubject<MatDrawerMode>('side');
  private readonly destroy$ = new Subject<void>();
  private readonly sidenavMargin = 300;
  private readonly xLargeMaterialBreakpoint = 1920;

  readonly opened$: Observable<boolean>;
  readonly leftNavCollapsed$: Observable<boolean>;
  readonly mode$: Observable<MatDrawerMode>;

  constructor() {
    this.opened$ = this._opened$.asObservable().pipe(shareReplay(1));
    this.leftNavCollapsed$ = this._leftNavCollapsed$.asObservable().pipe(shareReplay(1));
    this.mode$ = this._mode$.asObservable().pipe(shareReplay(1));
    this.breakpointObserver
      .observe([
        Breakpoints.Small,
        Breakpoints.XSmall,
        `(min-width: ${this.sidenavMargin * 2 + this.xLargeMaterialBreakpoint}px)`,
        Breakpoints.TabletLandscape,
      ])
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(({ matches }) => this._mode$.next(matches ? this.getOverDrawerMode() : this.getSideDrawerMode()));
  }

  setOpen(opened: boolean): void {
    this._opened$.next(opened);
  }

  toggleLeftNavCollapsed(): void {
    const isCollapsed = !this._leftNavCollapsed$.value;
    this._leftNavCollapsed$.next(isCollapsed);
    globalThis.localStorage?.setItem(this.leftNavCollapsedStorageKey, String(isCollapsed));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getOverDrawerMode(): MatDrawerMode {
    return 'over';
  }

  private getSideDrawerMode(): MatDrawerMode {
    return 'side';
  }

  private getInitialLeftNavCollapsed(): boolean {
    const storedValue = globalThis.localStorage?.getItem(this.leftNavCollapsedStorageKey);
    return storedValue === 'true';
  }
}
